import * as React from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Page, Layouts, useNotification } from '@strapi/strapi/admin';
import {
  Button,
  Box,
  Flex,
  Grid,
  TextInput,
  Field,
  Typography,
} from '@strapi/design-system';
import { ArrowLeft, Check, Eye } from '@strapi/icons';

import EasyEmailEditor, { type EasyEmailEditorHandle } from '../components/EasyEmailEditor';
import { useTemplatesApi, type EmailTemplate } from '../api/templates';
import { PLUGIN_ID } from '../pluginId';

interface EditorPageProps {
  mode: 'create' | 'edit';
}

const EMPTY_FORM: EmailTemplate = {
  name: '',
  subject: '',
  designJson: null,
  htmlBody: '',
};

interface StarterSeed {
  id: string;
  subject: string;
  content: any;
}

const EditorPage: React.FC<EditorPageProps> = ({ mode }) => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const api = useTemplatesApi();
  const { toggleNotification } = useNotification();

  // When the user picks a starter template in the modal, HomePage navigates here
  // with `location.state = { starter: { id, subject, content } }`. Capture it
  // once on first render — re-renders won't keep state across an editor mount.
  const starterSeed = React.useMemo<StarterSeed | null>(() => {
    const raw = (location.state as { starter?: StarterSeed } | null)?.starter;
    return raw && raw.content ? raw : null;
  }, []);

  const initialForm: EmailTemplate = React.useMemo(() => {
    if (mode === 'create' && starterSeed) {
      return {
        name: '',
        subject: starterSeed.subject ?? '',
        // Editor expects an `EmailTemplate` shape `{ subject, content }` for
        // initialValues — we wrap the starter accordingly downstream.
        designJson: { subject: starterSeed.subject ?? '', content: starterSeed.content },
        htmlBody: '',
      };
    }
    return EMPTY_FORM;
  }, [mode, starterSeed]);

  const editorRef = React.useRef<EasyEmailEditorHandle>(null);
  const [form, setForm] = React.useState<EmailTemplate>(initialForm);
  const [loading, setLoading] = React.useState(mode === 'edit');
  const [saving, setSaving] = React.useState(false);
  const [previewHtml, setPreviewHtml] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (mode !== 'edit' || !id) return;
    let cancelled = false;
    setLoading(true);
    api
      .getOne(id)
      .then(({ data }) => {
        if (!cancelled) setForm(data);
      })
      .catch((err: any) => {
        toggleNotification({
          type: 'danger',
          message:
            err?.message ??
            formatMessage({
              id: `${PLUGIN_ID}.editor.toast.loadError`,
              defaultMessage: 'Failed to load template',
            }),
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, id]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim()) {
      toggleNotification({
        type: 'warning',
        message: formatMessage({
          id: `${PLUGIN_ID}.editor.validation.required`,
          defaultMessage: 'Name and subject are required',
        }),
      });
      return;
    }

    if (!editorRef.current) return;

    setSaving(true);
    try {
      const designJson = editorRef.current.getValues();
      const htmlBody = await editorRef.current.exportHtml();

      const payload: EmailTemplate = {
        name: form.name.trim(),
        subject: form.subject.trim(),
        designJson,
        htmlBody,
      };

      const saved =
        mode === 'create'
          ? await api.create(payload)
          : await api.update(id as string, payload);

      toggleNotification({
        type: 'success',
        message: formatMessage({ id: `${PLUGIN_ID}.editor.toast.saved`, defaultMessage: 'Template saved' }),
      });

      if (mode === 'create' && saved.data?.documentId) {
        navigate(`../${saved.data.documentId}`, { replace: true });
      } else {
        setForm((prev) => ({ ...prev, ...saved.data }));
      }
    } catch (err: any) {
      toggleNotification({
        type: 'danger',
        message:
          err?.response?.data?.error?.message ??
          err?.message ??
          formatMessage({
            id: `${PLUGIN_ID}.editor.toast.savedError`,
            defaultMessage: 'Failed to save template',
          }),
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!editorRef.current) return;
    const html = await editorRef.current.exportHtml();
    setPreviewHtml(html);
  };

  if (loading) {
    return <Page.Loading />;
  }

  const headerTitle =
    mode === 'create'
      ? formatMessage({ id: `${PLUGIN_ID}.editor.title.new`, defaultMessage: 'New email template' })
      : form.name ||
        formatMessage({ id: `${PLUGIN_ID}.editor.title.edit`, defaultMessage: 'Edit email template' });

  return (
    <Page.Main>
      <Page.Title>{headerTitle}</Page.Title>
      <Layouts.Header
        navigationAction={
          <Link to=".." style={{ textDecoration: 'none' }}>
            <Flex gap={2} alignItems="center">
              <ArrowLeft />
              <Typography variant="omega">
                {formatMessage({ id: `${PLUGIN_ID}.editor.back`, defaultMessage: 'Back to list' })}
              </Typography>
            </Flex>
          </Link>
        }
        title={headerTitle}
        primaryAction={
          <Flex gap={2}>
            <Button variant="tertiary" startIcon={<Eye />} onClick={handlePreview}>
              {formatMessage({ id: `${PLUGIN_ID}.editor.preview`, defaultMessage: 'Preview' })}
            </Button>
            <Button startIcon={<Check />} loading={saving} onClick={handleSave}>
              {formatMessage({ id: `${PLUGIN_ID}.editor.save`, defaultMessage: 'Save' })}
            </Button>
          </Flex>
        }
      />

      <Layouts.Content>
        <Box paddingBottom={4}>
          <Grid.Root gap={4}>
            <Grid.Item col={6} s={12}>
              <Field.Root name="name" required>
                <Field.Label>
                  {formatMessage({ id: `${PLUGIN_ID}.editor.field.name`, defaultMessage: 'Internal name' })}
                </Field.Label>
                <TextInput
                  placeholder={formatMessage({
                    id: `${PLUGIN_ID}.editor.field.name.placeholder`,
                    defaultMessage: 'welcome-email',
                  })}
                  value={form.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </Field.Root>
            </Grid.Item>
            <Grid.Item col={6} s={12}>
              <Field.Root name="subject" required>
                <Field.Label>
                  {formatMessage({ id: `${PLUGIN_ID}.editor.field.subject`, defaultMessage: 'Subject line' })}
                </Field.Label>
                <TextInput
                  placeholder={formatMessage({
                    id: `${PLUGIN_ID}.editor.field.subject.placeholder`,
                    defaultMessage: 'Welcome to {{ company }}, {{ user.name }}!',
                  })}
                  value={form.subject}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((prev) => ({ ...prev, subject: e.target.value }))
                  }
                />
              </Field.Root>
            </Grid.Item>
          </Grid.Root>
        </Box>

        <Box background="neutral0" hasRadius shadow="filterShadow" overflow="hidden">
          <EasyEmailEditor
            ref={editorRef}
            initialValues={(form.designJson as any) ?? undefined}
          />
        </Box>

        {previewHtml !== null && (
          <Box marginTop={4} background="neutral0" hasRadius padding={4} shadow="filterShadow">
            <Flex justifyContent="space-between" alignItems="center" paddingBottom={2}>
              <Typography variant="delta">
                {formatMessage({ id: `${PLUGIN_ID}.editor.preview`, defaultMessage: 'Preview' })}
              </Typography>
              <Button variant="tertiary" onClick={() => setPreviewHtml(null)}>
                Close
              </Button>
            </Flex>
            <iframe
              title="Email preview"
              srcDoc={previewHtml}
              sandbox=""
              style={{ width: '100%', height: '600px', border: '1px solid #eaeaef', borderRadius: 4 }}
            />
          </Box>
        )}
      </Layouts.Content>
    </Page.Main>
  );
};

export default EditorPage;
